/**
 * Form-builder structure semantics — the single, layer-shared owner of the form-builder
 * wire⇄UI translation, validation predicates, and string-boolean coercion.
 *
 * Lives in `core/` (no DOM, no layer deps) so every layer can import it without a boundary
 * violation: the `components/` driver, the `pages/` codec wrapper, and the `pages/` factory all
 * import from here. The driver imports `normalizeFieldsets` + the predicates; only the codec
 * wrapper calls `parseStructure`/`serializeStructure`. Parity with `core/media/youtube-url.js`,
 * which both the videos driver and the videos codec share.
 *
 * **Keyless by contract.** Nothing here mints, reads, or depends on client keys. Client keys
 * (the `fs-`/`fd-`/`op-` reorder identities) are driver-only state. `serializeStructure` never
 * sees them; `normalizeFieldsets` never invents them.
 *
 * ## Legacy wire shape (DB-compatible — booleans are STRINGS)
 *
 * `form_structure` = array of fieldsets, each `{ fieldset_name, children: [...] }`:
 * - Simple (`input_text`|`email`|`textarea`): `{ cssClass, values: <label>, required: "true"|"false" }`
 * - Choice (`checkbox`|`radio`):            `{ cssClass, title, required, values: [{ value, baseline }] }`
 * - Select:                                 choice + `multiple: "true"|"false"`
 *
 * @module
 */

/**
 * @typedef {import('../../contracts/forms.js').FormBuilderOption} FormBuilderOption
 * @typedef {import('../../contracts/forms.js').FormBuilderField} FormBuilderField
 * @typedef {import('../../contracts/forms.js').FormBuilderFieldset} FormBuilderFieldset
 * @typedef {import('../../contracts/forms.js').FormBuilderCssClass} FormBuilderCssClass
 */

/** The six legacy field types, by `cssClass`. */
export const FIELD_TYPES = /** @type {const} */ (['input_text', 'email', 'textarea', 'checkbox', 'radio', 'select']);

/** Choice types carry a title + an options list (others carry a single label). */
export const CHOICE_TYPES = /** @type {const} */ (['checkbox', 'radio', 'select']);

/** @param {unknown} c @returns {c is FormBuilderCssClass} */
function isKnownType(c) {
  return typeof c === 'string' && /** @type {readonly string[]} */ (FIELD_TYPES).includes(c);
}

/** @param {FormBuilderCssClass} cssClass */
export function isChoiceType(cssClass) {
  return /** @type {readonly string[]} */ (CHOICE_TYPES).includes(cssClass);
}

// ---------------------------------------------------------------------------
// String-boolean coercion (the legacy wire stores "true"/"false"/"checked")
// ---------------------------------------------------------------------------

/**
 * Wire string → real boolean. Legacy `multiple` may be the artifact `"checked"`, which is
 * truthy; everything other than `"true"`/`"checked"` is false.
 * @param {unknown} s
 */
export function wireToBool(s) {
  return s === true || s === 'true' || s === 'checked';
}

/** Real boolean → wire string. Normalizes the legacy `"checked"` artifact to `"true"`. @param {unknown} b */
export function boolToWire(b) {
  return b ? 'true' : 'false';
}

/** Trimmed string accessor for a possibly-missing field. @param {Record<string, unknown>} o @param {string} k */
function str(o, k) {
  const v = o[k];
  return typeof v === 'string' ? v.trim() : '';
}

// ---------------------------------------------------------------------------
// Blank / invalid predicates (shared by serialize + the factory's onBeforeSubmit)
// ---------------------------------------------------------------------------

/**
 * An option row is blank when its `value` is empty (trimmed). Blank options are dropped on
 * serialize and ignored by the title/options validity checks.
 * @param {unknown} o
 */
export function isBlankOption(o) {
  if (!o || typeof o !== 'object') return true;
  return str(/** @type {Record<string, unknown>} */ (o), 'value') === '';
}

/** Non-blank options of a field (choice fields only; empty for simple fields). @param {FormBuilderField} f */
function nonBlankOptions(f) {
  return Array.isArray(f.options) ? f.options.filter((o) => !isBlankOption(o)) : [];
}

/**
 * A field is blank (→ dropped silently on serialize) when it carries no meaningful content:
 * - simple field: empty label.
 * - choice field: empty title AND no non-blank options.
 * @param {unknown} f
 */
export function isBlankField(f) {
  if (!f || typeof f !== 'object') return true;
  const field = /** @type {FormBuilderField} */ (f);
  if (!isKnownType(field.cssClass)) return true;
  if (isChoiceType(field.cssClass)) {
    return str(field, 'title') === '' && nonBlankOptions(field).length === 0;
  }
  return str(field, 'label') === '';
}

/**
 * A field is invalid (→ blocks Save) when it is not blank but half-specified. Only choice
 * fields can be invalid: a title with zero non-blank options, OR non-blank options with a
 * blank title. Simple fields are never invalid (blank-or-fine).
 * @param {unknown} f
 */
export function isInvalidField(f) {
  if (isBlankField(f)) return false;
  const field = /** @type {FormBuilderField} */ (f);
  if (!isChoiceType(field.cssClass)) return false;
  const hasTitle = str(field, 'title') !== '';
  const hasOptions = nonBlankOptions(field).length > 0;
  return hasTitle !== hasOptions; // exactly one present → invalid
}

/** Non-blank fields of a fieldset. @param {FormBuilderFieldset} fs */
function nonBlankFields(fs) {
  return Array.isArray(fs.fields) ? fs.fields.filter((f) => !isBlankField(f)) : [];
}

/** A fieldset is blank (→ dropped silently) when it has no non-blank fields. @param {unknown} fs */
export function isBlankFieldset(fs) {
  if (!fs || typeof fs !== 'object') return true;
  return nonBlankFields(/** @type {FormBuilderFieldset} */ (fs)).length === 0;
}

/** A fieldset is invalid when any of its non-blank fields is invalid. @param {unknown} fs */
export function isInvalidFieldset(fs) {
  if (!fs || typeof fs !== 'object') return false;
  return nonBlankFields(/** @type {FormBuilderFieldset} */ (fs)).some((f) => isInvalidField(f));
}

/**
 * The whole form is empty when it serializes to no non-blank fields across all fieldsets.
 * Used by the factory to block saving a meaningless empty form.
 * @param {unknown} fieldsets
 */
export function isEmptyForm(fieldsets) {
  if (!Array.isArray(fieldsets)) return true;
  return fieldsets.every((fs) => isBlankFieldset(fs));
}

// ---------------------------------------------------------------------------
// UI normalizer (driver + codec) — keyless, total, idempotent
// ---------------------------------------------------------------------------

/** Coerce an arbitrary value into a clean keyless `FormBuilderOption`. @param {unknown} o */
function normalizeOption(o) {
  const obj = /** @type {Record<string, unknown>} */ (o && typeof o === 'object' ? o : {});
  return /** @type {FormBuilderOption} */ ({
    value: typeof obj.value === 'string' ? obj.value : '',
    baseline: wireToBool(obj.baseline),
  });
}

/** Coerce an arbitrary value into a clean keyless `FormBuilderField`. @param {unknown} f */
function normalizeField(f) {
  const obj = /** @type {Record<string, unknown>} */ (f && typeof f === 'object' ? f : {});
  const cssClass = isKnownType(obj.cssClass) ? obj.cssClass : 'input_text';
  return /** @type {FormBuilderField} */ ({
    cssClass,
    label: typeof obj.label === 'string' ? obj.label : '',
    title: typeof obj.title === 'string' ? obj.title : '',
    required: wireToBool(obj.required),
    multiple: wireToBool(obj.multiple),
    options: Array.isArray(obj.options) ? obj.options.map(normalizeOption) : [],
  });
}

/**
 * Coerce an arbitrary already-UI-shape value into a clean keyless `FormBuilderFieldset[]`.
 * Total and idempotent; safe to run on `ctx.value`/`setValue` input. Never mints client keys.
 * @param {unknown} uiValue
 * @returns {FormBuilderFieldset[]}
 */
export function normalizeFieldsets(uiValue) {
  if (!Array.isArray(uiValue)) return [];
  return uiValue.map((fs) => {
    const obj = /** @type {Record<string, unknown>} */ (fs && typeof fs === 'object' ? fs : {});
    return /** @type {FormBuilderFieldset} */ ({
      name: typeof obj.name === 'string' ? obj.name : '',
      fields: Array.isArray(obj.fields) ? obj.fields.map(normalizeField) : [],
    });
  });
}

// ---------------------------------------------------------------------------
// Wire ⇄ UI (codec-only)
// ---------------------------------------------------------------------------

/** Wire option `{ value, baseline }` → UI option. @param {unknown} o */
function parseOption(o) {
  const obj = /** @type {Record<string, unknown>} */ (o && typeof o === 'object' ? o : {});
  return /** @type {FormBuilderOption} */ ({
    value: typeof obj.value === 'string' ? obj.value : '',
    baseline: wireToBool(obj.baseline),
  });
}

/** Wire child → UI field, dispatching on `cssClass`. @param {unknown} child */
function parseField(child) {
  const obj = /** @type {Record<string, unknown>} */ (child && typeof child === 'object' ? child : {});
  const cssClass = isKnownType(obj.cssClass) ? obj.cssClass : 'input_text';
  const required = wireToBool(obj.required);
  if (isChoiceType(cssClass)) {
    return /** @type {FormBuilderField} */ ({
      cssClass,
      label: '',
      title: typeof obj.title === 'string' ? obj.title : '',
      required,
      multiple: cssClass === 'select' ? wireToBool(obj.multiple) : false,
      options: Array.isArray(obj.values) ? obj.values.map(parseOption) : [],
    });
  }
  // simple types: `values` holds the label string
  return /** @type {FormBuilderField} */ ({
    cssClass,
    label: typeof obj.values === 'string' ? obj.values : '',
    title: '',
    required,
    multiple: false,
    options: [],
  });
}

/**
 * Wire `form_structure` → keyless UI model. Accepts a parsed array (mock backend stores it
 * inline) or a JSON string (production text column); a malformed string yields `[]`.
 * @param {unknown} wire
 * @returns {FormBuilderFieldset[]}
 */
export function parseStructure(wire) {
  let arr = wire;
  if (typeof arr === 'string') {
    try { arr = JSON.parse(arr); } catch { return []; }
  }
  if (!Array.isArray(arr)) return [];
  return arr.map((fs) => {
    const obj = /** @type {Record<string, unknown>} */ (fs && typeof fs === 'object' ? fs : {});
    return /** @type {FormBuilderFieldset} */ ({
      name: typeof obj.fieldset_name === 'string' ? obj.fieldset_name : '',
      fields: Array.isArray(obj.children) ? obj.children.map(parseField) : [],
    });
  });
}

/** UI option → wire option (drops blanks before this is called). @param {FormBuilderOption} o */
function serializeOption(o) {
  return { value: o.value, baseline: boolToWire(o.baseline) };
}

/** UI field → wire child (legacy keys only). @param {FormBuilderField} f */
function serializeField(f) {
  if (isChoiceType(f.cssClass)) {
    /** @type {Record<string, unknown>} */
    const out = {
      cssClass: f.cssClass,
      title: f.title.trim(),
      required: boolToWire(f.required),
      values: nonBlankOptions(f).map(serializeOption),
    };
    if (f.cssClass === 'select') out.multiple = boolToWire(f.multiple);
    return out;
  }
  return {
    cssClass: f.cssClass,
    values: f.label.trim(),
    required: boolToWire(f.required),
  };
}

/**
 * Keyless UI model → legacy wire array. Drops blank fieldsets / fields / option rows; emits
 * only legacy keys per type. Always returns an array (empty → `[]`, never `undefined`) so the
 * `form_structure` key is always sent and clearing persists.
 * @param {unknown} uiValue
 * @returns {Array<{ fieldset_name: string, children: Record<string, unknown>[] }>}
 */
export function serializeStructure(uiValue) {
  const fieldsets = normalizeFieldsets(uiValue);
  const out = [];
  for (const fs of fieldsets) {
    if (isBlankFieldset(fs)) continue;
    const children = nonBlankFields(fs).map(serializeField);
    out.push({ fieldset_name: fs.name.trim(), children });
  }
  return out;
}
